"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Lock, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getPublicGift, unlockGift, registerView } from "@/lib/api";
import { type Gift, PrivacyMode } from "@/types";
import { getTemplateConfig, type TemplateConfig } from "@/lib/templates";
import { PublicGiftRenderer } from "@/components/public-gift/PublicGiftRenderer";
import { cn } from "@/lib/utils";

interface PublicGiftPageProps {
  slug: string;
}

// Exemplo "Ana & Lucas" linkado pelo botão "Ver exemplo ao vivo" da landing.
// Renderiza um gift hardcoded sem bater na API — garante que o exemplo
// público sempre funcione independente do estado do backend.
const EXAMPLE_SLUG = "ana-e-lucas-3-anos";
const EXAMPLE_GIFT: Partial<Gift> = {
  slug: EXAMPLE_SLUG,
  title: "Nosso Amor em Cada Detalhe",
  partnerName: "Ana",
  recipientName: "Lucas",
  loveLetter:
    "Lucas, cada dia ao seu lado é um presente que eu carrego com gratidão no coração. Obrigada por escolher a gente todos os dias — e por transformar o ordinário em mágico. Te amo, hoje e sempre. 💕",
  musicName: "Perfect",
  artistName: "Ed Sheeran",
  musicUrl: "https://open.spotify.com/track/0tgVpDi06FyKpA1z0VMD4v",
  coupleStartDate: "2023-02-14",
  templateSlug: "purple-romantic",
  privacyMode: PrivacyMode.PUBLIC,
  photos: [
    {
      id: "demo-photo-1",
      giftId: "demo",
      url: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1200&q=80",
      width: 1200,
      height: 800,
      sizeBytes: 0,
      order: 0,
      createdAt: "2023-02-14T00:00:00.000Z",
    },
    {
      id: "demo-photo-2",
      giftId: "demo",
      url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80",
      width: 1200,
      height: 800,
      sizeBytes: 0,
      order: 1,
      createdAt: "2023-02-14T00:00:00.000Z",
    },
  ],
  timelineEvents: [],
};

// ─── Tela de senha ──────────────────────────────────────────────────────────────
function LockedScreen({
  cfg,
  onUnlock,
}: {
  cfg: TemplateConfig;
  onUnlock: (password: string) => Promise<string | null>;
}) {
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");

  const handleUnlock = async () => {
    if (!password) return;
    setUnlocking(true);
    setError("");
    const err = await onUnlock(password);
    if (err) setError(err);
    setUnlocking(false);
  };

  return (
    <div
      className={cn("min-h-screen flex flex-col items-center justify-center px-4", cfg.pageBgClass)}
      style={{ background: cfg.pageBackground }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("w-full max-w-sm text-center", cfg.fontClass)}
      >
        <div
          className="inline-flex items-center justify-center h-20 w-20 rounded-full shadow-xl mb-6"
          style={{ background: cfg.headerGradient }}
        >
          <Lock className={cn("h-10 w-10", cfg.headerTextClass)} />
        </div>
        <h1 className={cn("text-3xl font-black mb-2", cfg.bodyTextClass)}>Surpresa bloqueada</h1>
        <p className={cn("text-sm mb-8", cfg.bodyTextClass, "opacity-60")}>
          Esta página foi protegida com uma senha especial. Você sabe qual é? 🤭
        </p>
        <div className="flex flex-col gap-3">
          <Input
            type="password"
            placeholder="Digite a senha secreta..."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
            className="text-center text-lg tracking-widest"
            error={error}
            autoFocus
            autoComplete="off"
            aria-label="Senha de acesso"
          />
          <Button
            size="lg"
            loading={unlocking}
            onClick={handleUnlock}
            style={{ background: cfg.headerGradient, border: "none" }}
          >
            <Eye className="h-5 w-5" />
            Revelar surpresa
          </Button>
        </div>
        <p className={cn("text-xs mt-4 opacity-40", cfg.bodyTextClass)}>
          Dica: pode ser uma data especial do casal 💕
        </p>
      </motion.div>
    </div>
  );
}

// ─── Página principal ───────────────────────────────────────────────────────────
export function PublicGiftPage({ slug }: PublicGiftPageProps) {
  const [gift, setGift] = useState<Partial<Gift> | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://ilovepage.com.br/p/${slug}`;

  useEffect(() => {
    if (slug === EXAMPLE_SLUG) {
      setGift(EXAMPLE_GIFT);
      setLoading(false);
      return;
    }
    const fetchGift = async () => {
      await registerView(slug);
      const res = await getPublicGift(slug);
      if (!res.success) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const g = res.data.gift;
      if (g.privacyMode === PrivacyMode.PASSWORD) setLocked(true);
      setGift(g);
      setLoading(false);
    };
    fetchGift();
  }, [slug]);

  const cfg = getTemplateConfig(gift?.templateSlug);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8FA] to-[#FFE4E6] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
            <Heart className="h-12 w-12 text-[#E11D48] fill-[#E11D48]" />
          </motion.div>
          <p className="text-gray-500 text-sm">Carregando sua surpresa...</p>
        </div>
      </div>
    );
  }

  // ─── Not found ──────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8FA] via-[#FFE4E6] to-[#FFF7ED] flex flex-col items-center justify-center px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm"
        >
          <motion.div
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-white shadow-lg shadow-rose-200 mb-5"
          >
            <Heart className="h-10 w-10 text-rose-300 fill-rose-200" />
          </motion.div>
          <h1 className="text-2xl sm:text-3xl font-black text-[#1F2937] mb-2 leading-tight">
            Esta ILovePage não está disponível
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed">
            O link pode ter expirado, sido digitado incorretamente, ou a página
            ainda não foi publicada. Que tal criar a sua agora?
          </p>
          <Button className="mt-7" asChild size="lg">
            <a href="/">
              <Heart className="h-4 w-4 fill-white" />
              Criar minha ILovePage
            </a>
          </Button>
        </motion.div>
      </div>
    );
  }

  // ─── Locked ─────────────────────────────────────────────────────────────────
  if (locked) {
    return (
      <LockedScreen
        cfg={cfg}
        onUnlock={async (password) => {
          const res = await unlockGift(slug, { password });
          if (res.success) {
            // O backend devolve o gift COMPLETO no payload do unlock —
            // antes da senha, o getPublicBySlug retorna um stub mínimo
            // com tudo zerado (só title/templateSlug). Aqui trocamos
            // pelo gift cheio, senão o renderer fica sem música, sem
            // carta, sem experiências, etc.
            if (res.data.gift) setGift(res.data.gift);
            setLocked(false);
            return null;
          }
          return "Senha incorreta. Tente novamente.";
        }}
      />
    );
  }

  // ─── Renderiza o gift com o renderer compartilhado ─────────────────────────
  // PublicGiftRenderer decide internamente qual layout usar (Spotify, template
  // real ou layout default) e mantém paridade visual com o preview do wizard.
  return <PublicGiftRenderer gift={gift ?? {}} shareUrl={shareUrl} />;
}
